import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import { toast } from "react-toastify";
import { getUserLocation } from "../../components/getLocation";

const DEFAULT_LOCATION = {
  latitude: 5.0389,
  longitude: 7.9094,
};

const NIGERIA = "Nigeria";

export const UpdateLocation = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("gps");
  const [loading, setLoading] = useState(false);

  // Manual form fields
  const [form, setForm] = useState({
    address: "",
    city: "",
    state: "",
    country: NIGERIA,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateLocation = async () => {
    setLoading(true);

    if (mode === "gps") {
      let latitude = DEFAULT_LOCATION.latitude;
      let longitude = DEFAULT_LOCATION.longitude;

      try {
        const coords = await getUserLocation();
        if (
          coords &&
          typeof coords.latitude === "number" &&
          typeof coords.longitude === "number"
        ) {
          latitude = coords.latitude;
          longitude = coords.longitude;
        } else {
          toast.info("Using default location (Uyo)");
        }
      } catch (err) {
        console.warn("GPS failed, falling back to default:", err);
        toast.info("Location access failed. Using default (Uyo)");
      }

      try {
        const res = await api.kyc.updateLocation({
          latitude,
          longitude,
        });

        if (res?.status) {
          localStorage.setItem("hasLocation", "true");
          toast.success("Location updated successfully");
          navigate(-1);
        } else {
          toast.warn("Failed to update location");
        }
      } catch (err) {
        console.error(err);
        toast.warn("Failed to update location");
      }
    } else {
      // Manual mode
      const { address, city, state, country } = form;

      if (!city && !address) {
        toast.warn("Please enter at least city or address");
        setLoading(false);
        return;
      }

      try {
        const payload = {
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          country: country.trim() || NIGERIA,
        };

        const res = await api.kyc.updateLocation(payload);

        if (res?.status) {
          localStorage.setItem("hasLocation", "true");
          toast.success("Location saved successfully");
          navigate(-1);
        } else {
          toast.warn("Failed to save location");
        }
      } catch (err) {
        console.error(err);
        toast.warn("Failed to update location");
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-6 w-full max-w-md">

        {/* Top bar */}
        <div className="w-full flex items-center mt-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">
            Update Location
          </h1>
          <div className="w-9" />
        </div>

        {/* Icon */}
        <div className="mt-8 w-full flex justify-center">
          <div className="w-[96px] h-[96px] rounded-full bg-[#eadfb7] grid place-items-center border-[3px] border-[#cdb966]">
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#5d4037" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
            </svg>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex rounded-full bg-[#f5e8c7] p-1.5">
          <button
            type="button"
            onClick={() => setMode("gps")}
            className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition ${
              mode === "gps"
                ? "bg-[#5d4037] text-[#fff8e1] shadow-sm"
                : "text-[#5d4037] hover:bg-[#e0d2a8]"
            }`}
          >
            Use Current Location
          </button>
          <button
            type="button"
            onClick={() => setMode("manual")}
            className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition ${
              mode === "manual"
                ? "bg-[#5d4037] text-[#fff8e1] shadow-sm"
                : "text-[#5d4037] hover:bg-[#e0d2a8]"
            }`}
          >
            Enter Address Manually
          </button>
        </div>

        {/* Content */}
        <div className="mt-6 px-4">
          {mode === "gps" ? (
            <>
              <p className="text-center font-extrabold text-[18px]">
                Enable Your Location
              </p>
              <p className="mt-2 text-center text-[14px]">
                We’ll use your device location to personalize services and improve deliveries
              </p>
            </>
          ) : (
            <>
              <p className="text-center font-extrabold text-[18px]">
                Enter Your Location
              </p>
              <p className="mt-2 text-center text-[14px]">
                Type your address or city — especially helpful if GPS is inaccurate
              </p>

              <div className="mt-6 space-y-4">
                <input
                  type="text"
                  name="address"
                  placeholder="Street address (optional)"
                  value={form.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#cdb966] bg-white focus:outline-none focus:border-[#5d4037]"
                />
                <input
                  type="text"
                  name="city"
                  placeholder="City *"
                  value={form.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#cdb966] bg-white focus:outline-none focus:border-[#5d4037]"
                  required
                />
                <input
                  type="text"
                  name="state"
                  placeholder="State (e.g. Edo, Lagos)"
                  value={form.state}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#cdb966] bg-white focus:outline-none focus:border-[#5d4037]"
                />
                <input
                  type="text"
                  name="country"
                  placeholder="Country"
                  value={form.country}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#cdb966] bg-white focus:outline-none focus:border-[#5d4037]"
                />
              </div>
            </>
          )}
        </div>

        {/* Action Button */}
        <div className="mt-8 px-6">
          <button
            type="button"
            onClick={handleUpdateLocation}
            disabled={loading}
            className={`w-full py-3.5 rounded-full font-bold text-[16px] transition ${
              loading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#5d4037] text-[#fff8e1] hover:bg-[#4a352e]"
            }`}
          >
            {loading
              ? "Updating..."
              : mode === "gps"
              ? "Use Current Location"
              : "Save Location"}
          </button>
        </div>

        {/* Skip */}
        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="text-[14px] underline opacity-80 hover:opacity-100"
          >
            Skip for now
          </button>
        </div>

        <div className="mt-8 w-[260px] h-[8px] rounded-full bg-[#5d4037] mx-auto" />
      </div>
    </div>
  );
};